/**
 * LegalLayout — shared shell + typography for JAI's public legal pages
 * (Terms of Service, Privacy Policy).
 *
 * Public (no auth). Matches the JAI brand: navy headings, paper background,
 * Inter Tight body, mono labels. Exports small content primitives so the
 * Terms/Privacy pages stay readable and consistent.
 *
 * NOTE: <Ph> renders a highlighted placeholder (e.g. business name, ABN,
 * support email). Anything wrapped in <Ph> must be replaced with real
 * details before launch.
 */

import { Link } from 'react-router-dom'
import { JaiMark, JaiWordmark, JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { useBackToApp } from '../utils/appReturn'

const MAXW = 760
const BODY = 'rgba(10,31,68,0.80)'

const linkStyle = { color: JAI_COLORS.blue, textDecoration: 'none', fontWeight: 600, fontSize: 13 }

// ── Page shell ──────────────────────────────────────────────
export function LegalLayout({ title, updated, otherDoc, children }) {
  // Signed in → back to the screen they left; signed out → the landing page.
  const back = useBackToApp()

  return (
    <div style={{ minHeight: 'calc(100vh / var(--jai-zoom))', background: JAI_COLORS.paper, fontFamily: JAI_FONTS.tight, color: JAI_COLORS.ink }}>
      {/* Top bar */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(246,248,251,0.88)', backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${JAI_COLORS.hair}`,
      }}>
        <div style={{ maxWidth: MAXW, margin: '0 auto', padding: '13px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to={back.to} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <JaiMark size={30} />
            <JaiWordmark size={22} />
          </Link>
          <Link to={back.to} style={linkStyle}>{back.label} →</Link>
        </div>
      </header>

      {/* Title */}
      <div style={{ maxWidth: MAXW, margin: '0 auto', padding: '48px 24px 4px' }}>
        <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: JAI_COLORS.mute, marginBottom: 12 }}>
          JAI · Legal
        </div>
        <h1 style={{ fontSize: 38, fontWeight: 750, letterSpacing: '-0.03em', color: JAI_COLORS.navy, margin: 0, lineHeight: 1.05 }}>
          {title}
        </h1>
        <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 12, color: JAI_COLORS.mute, marginTop: 14 }}>
          Last updated: {updated}
        </div>
      </div>

      {/* Content */}
      <main style={{ maxWidth: MAXW, margin: '0 auto', padding: '24px 24px 56px' }}>
        {children}
      </main>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${JAI_COLORS.hair}`, background: JAI_COLORS.white }}>
        <div style={{ maxWidth: MAXW, margin: '0 auto', padding: '26px 24px', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12.5, color: JAI_COLORS.mute }}>© 2026 JAI · Smarter fantasy picks</div>
          <div style={{ display: 'flex', gap: 20 }}>
            {otherDoc && <Link to={otherDoc.to} style={linkStyle}>{otherDoc.label}</Link>}
            <Link to={back.to} style={linkStyle}>Back to JAI</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ── On-this-page nav ────────────────────────────────────────
export function TOC({ items }) {
  return (
    <nav style={{ background: JAI_COLORS.white, border: `1px solid ${JAI_COLORS.hair}`, borderRadius: 14, padding: '16px 18px', margin: '8px 0 30px' }}>
      <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: JAI_COLORS.mute, marginBottom: 12 }}>
        On this page
      </div>
      <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '7px 24px' }}>
        {items.map((it, i) => (
          <li key={it.href} style={{ fontSize: 13, lineHeight: 1.4 }}>
            <a href={it.href} style={{ color: JAI_COLORS.blue, textDecoration: 'none' }}>
              <span style={{ color: JAI_COLORS.mute, fontFamily: JAI_FONTS.mono, marginRight: 8 }}>{String(i + 1).padStart(2, '0')}</span>
              {it.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

// ── Content primitives ──────────────────────────────────────
export function H2({ id, children }) {
  return (
    <h2 id={id} style={{ fontSize: 20, fontWeight: 700, color: JAI_COLORS.navy, letterSpacing: '-0.015em', margin: '36px 0 10px', scrollMarginTop: 78 }}>
      {children}
    </h2>
  )
}

export function H3({ children }) {
  return <h3 style={{ fontSize: 15.5, fontWeight: 700, color: JAI_COLORS.navy, margin: '22px 0 6px' }}>{children}</h3>
}

export function P({ children }) {
  return <p style={{ fontSize: 15, lineHeight: 1.65, color: BODY, margin: '0 0 14px' }}>{children}</p>
}

export function UL({ children }) {
  return <ul style={{ margin: '0 0 16px', paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</ul>
}

export function LI({ children }) {
  return <li style={{ fontSize: 15, lineHeight: 1.6, color: BODY }}>{children}</li>
}

// Outbound link inside legal copy — e.g. a regulator, or a third party's own
// opt-out page. Underlined rather than colour-only so it's still obviously a
// link for anyone who can't distinguish the blue.
export function A({ children, href, ...rest }) {
  return (
    <a
      href={href}
      style={{ color: JAI_COLORS.blue, textDecoration: 'underline' }}
      {...rest}
    >
      {children}
    </a>
  )
}

export function Strong({ children }) {
  return <strong style={{ color: JAI_COLORS.navy, fontWeight: 700 }}>{children}</strong>
}

// Highlighted placeholder — replace before launch.
export function Ph({ children }) {
  return (
    <span style={{
      color: JAI_COLORS.blue,
      padding: '1px 2px', borderRadius: 0, fontFamily: JAI_FONTS.mono,
      fontSize: '0.85em', fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  )
}

// Emphasis box. tone: 'info' (blue) or 'warn' (red).
export function Callout({ tone = 'info', children }) {
  const rule = tone === 'warn' ? '#D63A3A' : '#1E5BD6'
  return (
    <div style={{ borderLeft: `3px solid ${rule}`, borderRadius: 0, padding: '2px 0 4px 18px', margin: '6px 0 22px' }}>
      {children}
    </div>
  )
}
